import React from 'react';
import { CONTENT_MAX_WIDTH } from '@leagueplatform/web-common-components';
import { GDSColor, StackLayout } from '@leagueplatform/genesis-core';
import { AssetLink, AssetLinkProps } from '../asset-links/asset-link.component';
import { FooterLinkProps } from './footer-items/footer-link-items.component';
import { FooterLinksAndIconsSection } from './footer-items/footer-links-container.component';
import { useIsBelowTablet } from '../../hooks/use-is-below-tablet/use-is-below-tablet.hook';

const FooterLayoutWrapper = ({
  children,
  backgroundColor,
}: {
  children: React.ReactNode;
  backgroundColor: GDSColor;
}) => (
  <StackLayout
    css={{
      '@laptop': {
        padding: '$one $one',
      },
      '@desktop': {
        padding: '$none $five',
      },
      backgroundColor,
    }}
  >
    {children}
  </StackLayout>
);

export const Footer = ({
  footerAppDownloadAssets,
  branding,
  footerLinks,
  secondaryFooterLinks,
  footerIcons,
  backgroundColor = '$surfaceBackgroundSecondary',
  textColor,
  footerDescription,
}: {
  footerAppDownloadAssets: AssetLinkProps[];
  branding?: React.ReactNode;
  footerLinks: FooterLinkProps[];
  secondaryFooterLinks?: FooterLinkProps[];
  footerIcons?: AssetLinkProps[];
  backgroundColor?: GDSColor;
  textColor?: GDSColor;
  footerDescription?: React.ReactNode;
}) => {
  const isBelowTablet = useIsBelowTablet();

  return (
    <FooterLayoutWrapper backgroundColor={backgroundColor}>
      <StackLayout
        orientation={{
          '@initial': 'vertical',
          '@laptop': 'horizontal',
          '@desktop': 'horizontal',
        }}
        horizontalAlignment="center"
        css={{
          maxWidth: isBelowTablet ? 'none' : CONTENT_MAX_WIDTH,
          margin: 'auto',
        }}
      >
        {/* Branding + App Download Links Section */}
        <StackLayout
          spacing="$one"
          css={{
            flexGrow: 0,
            margin: '$oneAndHalf $one $one $one',
            '@laptop': {
              margin: '$twoAndHalf',
            },
            '@desktop': {
              margin: '$three $none',
            },
          }}
        >
          {!!branding && branding}
          <StackLayout spacing="$half">
            {footerAppDownloadAssets.map((item) => (
              <AssetLink
                key={item.url}
                src={item.src}
                alt={item.alt}
                url={item.url}
              />
            ))}
          </StackLayout>
        </StackLayout>

        {/* External and Social Links and Disclaimer Section */}
        <StackLayout
          spacing="$one"
          css={{
            margin: '$one $oneAndHalf',
            maxWidth: CONTENT_MAX_WIDTH,
            '@laptop': {
              margin: '$twoAndHalf $one',
            },
            '@desktop': {
              margin: '$three 20px',
            },
          }}
        >
          <FooterLinksAndIconsSection
            footerLinks={footerLinks}
            secondaryFooterLinks={secondaryFooterLinks}
            footerIcons={footerIcons}
            textColor={textColor}
          />
          {footerDescription}
        </StackLayout>
      </StackLayout>
    </FooterLayoutWrapper>
  );
};
